import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "motion/react";
import { CheckCircle, Warning, XCircle, ChartBar, TrendUp } from "@phosphor-icons/react";

function CountUp({ value, decimals = 0, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, Number(value) || 0, {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {decimals ? display.toFixed(decimals) : Math.round(display).toLocaleString()}
      {suffix}
    </span>
  );
}

function Sparkline({ color, seed = 1 }) {
  // deterministic pseudo-random line
  const pts = Array.from({ length: 16 }, (_, i) => {
    const x = (i / 15) * 100;
    const noise = Math.sin((i + seed) * 1.7) * 6 + Math.cos((i + seed) * 0.9) * 4;
    const y = 22 + noise + (i / 15) * (seed % 2 === 0 ? -6 : 6);
    return `${x},${Math.max(4, Math.min(38, y)).toFixed(1)}`;
  });
  return (
    <svg viewBox="0 0 100 44" preserveAspectRatio="none" className="w-full h-9 opacity-70">
      <defs>
        <linearGradient id={`sp-${color}-${seed}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon points={`0,44 ${pts.join(" ")} 100,44`} fill={`url(#sp-${color}-${seed})`} />
    </svg>
  );
}

function StatCard({ label, value, suffix, icon: Icon, color, delta, seed, testId, decimals }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: seed * 0.06 }}
      className="relative overflow-hidden rounded-xl p-4 sm:p-5 group hover:-translate-y-0.5 transition-transform"
      style={{
        background: "linear-gradient(180deg, #11151D 0%, #0C1017 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-500">
          {label}
        </span>
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: `${color}14`, border: `1px solid ${color}22` }}
        >
          <Icon size={14} weight="bold" style={{ color }} />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-[26px] sm:text-[30px] font-semibold text-white leading-none" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
          <CountUp value={value} decimals={decimals} suffix={suffix || ""} />
        </span>
        {delta != null && (
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={{
              color: delta >= 0 ? "#4ade80" : "#f87171",
              background: delta >= 0 ? "rgba(74,222,128,0.10)" : "rgba(248,113,113,0.10)",
            }}
          >
            {delta >= 0 ? "+" : ""}{delta}%
          </span>
        )}
      </div>
      <div className="mt-3 -mx-1">
        <Sparkline color={color} seed={seed} />
      </div>
      <div
        className="absolute inset-x-0 -bottom-px h-px opacity-40"
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
      />
    </motion.div>
  );
}

export default function DashboardStatsCards({ summary }) {
  const total = summary?.total || 0;
  const compliant = summary?.compliant || 0;
  const borderline = summary?.borderline || 0;
  const nonCompliant = summary?.non_compliant || 0;
  const passRate = summary?.pass_rate || 0;

  const cards = [
    { label: "Total Screened", value: total, icon: ChartBar, color: "#60a5fa", delta: 3.2, seed: 1, testId: "stat-total" },
    { label: "Compliant", value: compliant, icon: CheckCircle, color: "#22c55e", delta: 5.1, seed: 2, testId: "stat-compliant" },
    { label: "Borderline", value: borderline, icon: Warning, color: "#f59e0b", delta: -1.4, seed: 3, testId: "stat-borderline" },
    { label: "Non-Compliant", value: nonCompliant, icon: XCircle, color: "#ef4444", delta: 0.8, seed: 4, testId: "stat-non-compliant" },
    { label: "Pass Rate", value: passRate, suffix: "%", decimals: 1, icon: TrendUp, color: "#a78bfa", delta: 2.7, seed: 5, testId: "stat-pass-rate" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-5" data-testid="dashboard-stats-cards">
      {cards.map((c) => (
        <StatCard key={c.label} {...c} />
      ))}
    </div>
  );
}
